// @flow
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import classnames from 'classnames';
import { Icon, Button } from '@elements';
import { fetchGoogleCalendarStatus } from '@context/googleCalendar';
import useGoogleCalendarConnect from '../../../Settings/GoogleCalendar/useGoogleCalendarConnect';

type Props = {
  doctorId: ?(number | string),
  // The appointment's professional has Google Agenda connected in this clinic: saved without a link, it gets a Meet link
  googleMeet: boolean,
  // A blank link field means the professional's fixed room: nothing to paste meanwhile
  roomLink: boolean,
  _: Function,
};

// What Google Agenda does for an online appointment without a link of its own (OnlineModal):
// - connected: saving creates its Google Meet link;
// - the signed-in doctor's own appointment, not connected, in a clinic with Google Agenda: connect from here.
//   The Google page opens in the browser, so the appointment being written stays open; on return the status
//   is loaded again and, connected, the card says the link is created on save;
// - anyone else's (a secretary, a colleague): only the professional can connect, from their own account.
// Nothing where Google Agenda can't be used: the clinic doesn't have it (turned off in the admin, a plan without
// it), or the API can't connect Google accounts (`available` false).
const GoogleMeetCard = ({ doctorId, googleMeet, roomLink, _ }: Props) => {
  const dispatch = useDispatch();
  // `_` takes a namespace, not values: the professional's name goes through i18next's own t
  const { t } = useTranslation();
  const status = useSelector(state => get(state, ['context', 'googleCalendar'], null));
  const isDoctor = useSelector(state => get(state, ['context', 'user', 'roleType']) === 'Doctor');
  const signedInDoctorId = useSelector(state => get(state, ['context', 'user', 'advancedData', 'doctorId'], null));
  const doctorName = useSelector(state => get(state, ['context', 'doctors', 'data', String(doctorId), 'nameWithTitle']) || null);
  const { opened, connect } = useGoogleCalendarConnect();
  const connected = get(status, 'connected') === true;

  // A fresh status, not a stored one: a connection finished elsewhere (another tab, after closing this screen)
  // shows at once. Not needed once the link is created on save.
  useEffect(() => {
    if (!googleMeet) dispatch(fetchGoogleCalendarStatus());
  }, [dispatch, googleMeet]);

  // Back from Google's page: resume, visibilitychange and focus fire at once, and fetchGoogleCalendarStatus
  // shares the request. Until the status says connected.
  useEffect(() => {
    if (!opened || connected) return undefined;

    const refresh = () => dispatch(fetchGoogleCalendarStatus());
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('resume', refresh);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', refresh);

    return () => {
      document.removeEventListener('resume', refresh);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', refresh);
    };
  }, [dispatch, opened, connected]);

  const ownAppointment = isDoctor && signedInDoctorId != null && String(signedInDoctorId) === String(doctorId);
  const usable = get(status, 'available') === true && get(status, 'clinicActivated') === true;

  let text = null;
  let note = null;
  let offerConnect = false;
  if (googleMeet) {
    text = _('label/online/google_meet');
  } else if (!usable) {
    return null;
  } else if (ownAppointment) {
    // Connected but not yet in this clinic's list, or a status saying it can't connect: nothing to offer
    if (connected || get(status, 'canConnect') !== true) return null;
    // Connected before, but the access was removed or the "Doutore" calendar deleted
    text = _(get(status, 'needsReconnect') === true ? 'label/online/google_reconnect' : 'label/online/google_connect');
    if (opened) note = _('label/online/google_opened');
    offerConnect = true;
  } else {
    text = doctorName
      ? t('agenda:label/online/google_doctor', { doctor: doctorName })
      : _('label/online/google_doctor_unnamed');
    if (!roomLink) text = `${text} ${_('label/online/google_until_then')}`;
  }

  return (
    <div className={classnames('onlineGoogleCard', { ready: googleMeet })}>
      <span className="onlineGoogleCard-icon" aria-hidden="true">
        <Icon iconName={googleMeet ? 'check-circle' : 'event_available'} size={22} />
      </span>
      <div className="onlineGoogleCard-body">
        <div role="status">
          <p className="onlineGoogleCard-title">{_('label/online/google_title')}</p>
          <p className="onlineGoogleCard-text">{text}</p>
          {note && <p className="onlineGoogleCard-opened">{note}</p>}
        </div>
        {offerConnect && (
          <Button className="primary onlineGoogleCard-connect" onClick={connect}>
            {_('action/online/connect_google')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default GoogleMeetCard;
